import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { get, uniqueId } from "lodash";
import axios from "axios";
import {
  showErrorModal,
  updateApplicationTitle,
  updatePreventScrollOnRouteChange,
  useApplicationState,
} from "../../utilities/applicationState";
import ManageProject from "./manageProject";
import TestInstance from "./testInstance";
import ProjectContext from "./projectContext";
import ProjectTitle from "./projectTitle";
import Subscriptions from "./subscriptions";
import { CustomToolTip } from "shared-one-ux-components";
import {
  SdfBox,
  SdfBusyIndicator,
  SdfIcon,
  SdfLink,
  SdfQuickStat,
  SdfTab,
  SdfTabGroup,
} from '@synerg/react-components';
import Credentials from "./credentials";
import { apiUtils } from "shared-one-ux-components";
import Contextualize from "./contextualize/Contextualize";
import Toast from "../common/toast";
import { webhookUIBaseUrl, webhookAppShellBaseUrl } from "../../config";

export default function ProjectDetails() {
  const [, dispatch] = useApplicationState();
  const history = useHistory();
  const { credentials, projectId } = useParams();
  const [projectDetail, setProjectDetail] = useState({});
  const [linkedProject, setLinkedProject] = useState({});
  const [hasPageNavigated, setHasPageNavigated] = useState(false);

  const {
    legacy,
    caseDisplay,
    appStore,
    type,
    status,
    platform,
    b2bConsumerAppID,
  } = projectDetail;

  const [loading, setLoading] = useState(false);
  const [fetchingApis, setFetchingApis] = useState(false);
  const [toast, setToast] = useState(false);
  const [filteredApis, setFilteredApis] = useState([]);
  const hasFilteredApis = filteredApis.length > 0 && !loading;
  const [events, setEvents] = useState("");

  const handleResponse = (message, status = "error") => {
    setToast({
      key: uniqueId(),
      status: status,
      message: message,
      showToast: true,
    });
  };
  const tabNames = (type = "DEV") => {
    return {
      projects:
        type === "DEV"
          ? "Manage Development Project"
          : "Manage Production Project",
      credentials:
        type === "DEV" ? "Development Credentials" : "Production Credentials",
      apis: type === "DEV" ? "Development APIs" : "Production APIs",
      subscriptions:
        type === "DEV" ? "Test Subscriptions" : "Live Subscriptions",
    };
  };

  const [selectedTab, setSelectedTab] = useState("");
  const isTabCredentialsActive = credentials === "credentials";
  const isTabTestInstanceActive = credentials === "test";
  const isSubscriptionTabActive = credentials === "subscriptions";
  const isApisTabActive = credentials === "apis";
  const isSuspended = status === "SUSPENDED";
  const isDataCloudProject = platform === "datacloud";
  const [endUserApiTabActive, setEndUserApiTabActive] = useState(0);
  const popularProducts = ["small", "mid", "midng", "large"];

  const goBack = () => {
    history.push("/projects");
  };
  const goToCredentials = () => {
    history.push(`/projects/${projectId}/credentials`);
  };
  const goToManage = () => {
    history.push(`/projects/${projectId}`);
  };
  const goToTestInstance = () => {
    history.push(`/projects/${projectId}/test`);
  };
  const goToSubscriptions = () => {
    history.push(`/projects/${projectId}/subscriptions`);
  };
  const goToApis = () => {
    history.push(`/projects/${projectId}/apis`);
  };

  const preventScroll = () => {
    dispatch(updatePreventScrollOnRouteChange(true));
  };

  async function getProjectDetail() {
    setLoading(true);
    try {
      const { data } = await axios(
        `${import.meta.env.VITE_ROOT_PATH}/api/project?projectID=${projectId}&isProjectPage=true`
      );
      setProjectDetail(data);
      if (data.associatedProject) {
        setLinkedProject(data.associatedProject);
      }
      setLoading(false);
    } catch (e) {
      const errorMessage = get(
        e,
        "response.data.message",
        "Something went wrong when trying to get project details. Please try again."
      );

      if (errorMessage.toLowerCase().includes("access denied")) {
        history.push("/access-denied?error=unauthorized");
      } else {
        dispatch(showErrorModal(errorMessage));
        setLoading(false);
      }
    }
  }

  async function getProjectConsumerAppDetails() {
    const { _id: projectId, b2bConsumerAppID } = projectDetail;
    if (projectDetail.b2bConsumerAppID && !projectDetail.updated) {
      try {
        setFetchingApis(true);
        const { data } = await axios.get(
          `${import.meta.env.VITE_ROOT_PATH}/api/project/consumer-application?consumerApplicationID=${b2bConsumerAppID}&projectID=${projectId}`
        );
        if (data) {
          const res = apiUtils.formatAPIDetails(data);
          setFilteredApis(res.filteredApis);
          setEvents(res.events);
        }

        setFetchingApis(false);
      } catch (e) {
        const errorMessage = get(
          e,
          "response.data.message",
          "Something went wrong when trying to get project details. Please try again."
        );

        if (errorMessage.toLowerCase().includes("access denied")) {
          history.push("/access-denied?error=unauthorized");
        } else {
          handleResponse(errorMessage);
          setFetchingApis(false);
        }
      }
    }
  }

  async function getProjectClientCred() {
    const { _id: projectId, b2bConsumerAppID } = projectDetail;
    if (projectDetail.b2bConsumerAppID && !projectDetail.updated) {
      try {
        setLoading(true);
        await axios.get(
          `${import.meta.env.VITE_ROOT_PATH}/api/project/credentials/client-id?consumerApplicationID=${b2bConsumerAppID}&projectID=${projectId}`
        );
        // const {data} = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/credentials/client-id?consumerApplicationID=${b2bConsumerAppID}&projectID=${projectId}`);
        // if (data) {
        // 	const clientID = get(data, 'clientID');
        // }
        setLoading(false);
      } catch (e) {
        const errorMessage = get(
          e,
          "response.data.message",
          "Something went wrong when trying to get project details. Please try again."
        );
        if (errorMessage.toLowerCase().includes("access denied")) {
          history.push("/access-denied?error=unauthorized");
        } else {
          handleResponse(errorMessage);
          setLoading(false);
        }
      }
    }
  }

  useEffect(() => {
    getProjectDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getProjectConsumerAppDetails();
    getProjectClientCred();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetail]);

  useEffect(() => {
    setEndUserApiTabActive(b2bConsumerAppID ? 0 : 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b2bConsumerAppID]);

  useEffect(() => {
    if (type !== undefined) {
      if (
        isTabTestInstanceActive & (platform === "marketplace") &&
        !isSuspended
      ) {
        setSelectedTab("Test Instance");
      } else if (isTabCredentialsActive && !isSuspended) {
        setSelectedTab(tabNames(type).credentials);
      } else if (isSubscriptionTabActive) {
        setSelectedTab(tabNames(type).subscriptions);
      } else if (isApisTabActive) {
        setSelectedTab(tabNames(type).apis);
      } else {
        setSelectedTab(tabNames(type).projects);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (hasPageNavigated) {
      getProjectDetail();
      setHasPageNavigated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPageNavigated]);

  // change title on mount
  useEffect(() => {
    dispatch(updateApplicationTitle("Projects"));
    dispatch(updatePreventScrollOnRouteChange(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectedTab = (event, type) => {
    if (event.detail.value) {
      setSelectedTab(event.detail.value);
      switch (event.detail.value) {
        case tabNames(type).projects:
          goToManage();
          break;
        case tabNames(type).credentials:
          goToCredentials();
          break;
        case "Test Instance":
          goToTestInstance();
          break;
        case tabNames(type).subscriptions:
          goToSubscriptions();
          break;
        case tabNames(type).apis:
          goToApis();
          break;
        default:
          goToManage();
          break;
      }
    }
  };

  return (
    <ProjectContext.Provider value={{ projectDetail, setProjectDetail }}>
      <div className="flex-col mx-24 my-10">
        <SdfLink
          className="cursor-pointer"
          slot="breadcrumb"
          size="lg"
          onClick={goBack}
        >
          <SdfIcon icon="nav-move-left" className="hydrated" />
          Back to all projects
        </SdfLink>

        <div className="flex flex-row justify-between items-center mt-6">
          <div className="w-1/3">
            <ProjectTitle
              projectDetail={projectDetail}
              setProjectDetail={setProjectDetail}
              status={status}
              legacy={legacy}
            />
          </div>

          <div className="w-2/3 relative" style={{ bottom: "0.5rem" }}>
            {type !== undefined && (
              <React.Fragment>
                <SdfBox
                  className="flex flex-row justify-between col-gap-2 items-center pb-4 px-2"
                  style={{ height: "6.25rem" }}
                >
                  <SdfQuickStat
                    className={`${
                      platform === "datacloud" ? "max-w-2/3" : "max-w-1/3"
                    }`}
                    labelPosition="bottom"
                    label="Use Case"
                  >
                    {legacy && (
                      <CustomToolTip
                        text="Legacy project No use case"
                        startEllipsisLen={10}
                      />
                    )}
                    {platform === "datacloud" ? (
                      <div className="text-heading-05">{caseDisplay}</div>
                    ) : (
                      <div>
                        {" "}
                        <CustomToolTip
                          className="text-heading-05"
                          text={caseDisplay && caseDisplay}
                          startEllipsisLen={20}
                          width="100%"
                        />{" "}
                      </div>
                    )}
                  </SdfQuickStat>
                  <SdfQuickStat
                    className="max-w-1/2"
                    labelPosition="bottom"
                    label="ADP Product"
                  >
                    <CustomToolTip
                      className="text-heading-05"
                      text={
                        projectDetail.product
                          ? projectDetail.product.name
                          : "Other"
                      }
                      startEllipsisLen={18}
                      width="100%"
                    />
                  </SdfQuickStat>

                  {platform !== "datacloud" && (
                    <SdfQuickStat
                      className="max-w-1/3"
                      labelPosition="bottom"
                      label="Locale"
                    >
                      <div className="text-heading-05">
                        {appStore ? appStore + " Marketplace" : "None"}
                      </div>
                    </SdfQuickStat>
                  )}
                </SdfBox>
              </React.Fragment>
            )}
          </div>
        </div>

        {type !== undefined && (
          <div className="mt-12">
            <SdfTabGroup onSdfChange={($event) => onSelectedTab($event, type)}>
              <SdfTab
                value={tabNames(type).projects}
                active={selectedTab === tabNames(type).projects}
              >
                {tabNames(type).projects}
              </SdfTab>
              {!isSuspended && (
                <SdfTab
                  value={tabNames(type).credentials}
                  active={selectedTab === tabNames(type).credentials}
                >
                  {tabNames(type).credentials}
                </SdfTab>
              )}
              <SdfTab
                value={tabNames(type).apis}
                active={selectedTab === tabNames(type).apis}
              >
                {tabNames(type).apis}
              </SdfTab>
              {type === "DEV" &&
                platform === "marketplace" &&
                projectDetail.testOrganizationOID &&
                !isSuspended &&
                popularProducts.includes(
                  get(projectDetail, "product.sizeName")
                ) && (
                  <SdfTab
                    value="Test Instance"
                    active={selectedTab === "Test Instance"}
                  >
                    Test Instance
                  </SdfTab>
                )}
              <SdfTab
                value={tabNames(type).subscriptions}
                active={selectedTab === tabNames(type).subscriptions}
              >
                {tabNames(type).subscriptions}
              </SdfTab>
            </SdfTabGroup>

            {selectedTab === tabNames(type).projects ? (
              <>
                <ManageProject
                  associatedProject={linkedProject}
                  onNavigation={preventScroll}
                  setHasPageNavigated={setHasPageNavigated}
                  isDataCloudProject={isDataCloudProject}
                  setSelectedTab={setSelectedTab}
                  projectDetail={projectDetail}
                />
              </>
            ) : null}
            {selectedTab === tabNames(type).credentials && (
              <Credentials
                setSelectedTab={setSelectedTab}
                setEndUserApiTabActive={setEndUserApiTabActive}
              />
            )}
            <>
              {selectedTab === "Development APIs" && !fetchingApis && window.SERVER_ENV &&
                <Contextualize
                  loading={fetchingApis}
                  projectDetail={projectDetail}
                  endUserApiTabActive={endUserApiTabActive}
                  setEndUserApiTabActive={setEndUserApiTabActive}
                  events={events}
                  projectType={"self_service"}
                  filteredApis={filteredApis}
                  setFilteredApis={setFilteredApis}
                  hasFilteredApis={hasFilteredApis}
                  title="Development APIs"
                  webhookUIBaseUrl={webhookUIBaseUrl[import.meta.env.VITE_ENVIRONMENT]}
                  webhookAppShellBaseUrl={webhookAppShellBaseUrl[import.meta.env.VITE_ENVIRONMENT]}
                  selectEventsTab={null}
                />
              }
              {selectedTab === "Production APIs" && !fetchingApis && window.SERVER_ENV &&
                <Contextualize
                  loading={fetchingApis}
                  projectDetail={projectDetail}
                  endUserApiTabActive={endUserApiTabActive}
                  setEndUserApiTabActive={setEndUserApiTabActive}
                  events={events}
                  projectType={"self_service"}
                  filteredApis={filteredApis}
                  setFilteredApis={setFilteredApis}
                  hasFilteredApis={hasFilteredApis}
                  title="Production APIs"
                  webhookUIBaseUrl={webhookUIBaseUrl[import.meta.env.VITE_ENVIRONMENT]}
                  webhookAppShellBaseUrl={webhookAppShellBaseUrl[import.meta.env.VITE_ENVIRONMENT]}
                  selectEventsTab={null}
                />
              }
              {(selectedTab === "Production APIs" ||
                selectedTab === "Development APIs") &&
                fetchingApis && (
                  <SdfBusyIndicator size={"md"} overlay={false} />
                )}
            </>

            {selectedTab === "Test Instance" && <TestInstance />}
            {selectedTab === tabNames(type).subscriptions && (
              <Subscriptions projectDetail={projectDetail} type={type} />
            )}
          </div>
        )}
        {loading && <SdfBusyIndicator size={"lg"} overlay={true} />}
      </div>
      {toast && (
        <Toast
          key={toast.key}
          status={toast.status}
          message={toast.message}
          showToast={toast.showToast}
        />
      )}
    </ProjectContext.Provider>
  );
}
